"use client";

import { forwardRef, useId } from "react";
import styled from "styled-components";
import { spacing4, spacing8, textColorPrimary, textColorTertiary } from "@churchofjesuschrist/eden-style-constants";
import { InternalUseOnlyLabel } from "./Label";
import { InternalUseOnlyRadio } from "./Radio";
import { InternalUseOnlyCheckbox } from "./Checkbox";
import { ValidityStateContext } from "../utils/context";
import Legend, { InternalUseOnlyLegend } from "./Legend";
import Hint, { InternalUseOnlyHint } from "./Hint";
import RequiredIndicator from "./RequiredIndicator";
import Tooltip from "./Tooltip";
import { ConditionalWrapper, deepMapChildren } from "@churchofjesuschrist/eden-utilities";
import Row from "@churchofjesuschrist/eden-row";
import useValidation, { getValidityState } from "../utils/useValidation";
import { valuesByValidityState } from "../utils/sharedCss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Container = styled.fieldset.withConfig({
  componentId: "sc-1e7bazx-0"
})(["display:flex;flex-wrap:wrap;align-items:center;border:none;padding:0;margin:0;& > :is(legend,label,input){flex:0 1 100%;padding:", " ", ";}& > :first-child{padding-block-start:0;}& > :last-child{padding-block-end:0;}& ", "{display:flex;gap:", ";}& > ", "{position:relative;display:flex;gap:", ";color:", ";padding:", ";&::before{content:\"\";position:absolute;inset-block-start:0;inset-inline-start:0;inset-inline-end:0;inset-block-end:0;border:1px solid ", ";}&:not(:first-of-type)::before{border-block-start:0;}&:not(:last-of-type)::before{border-block-end:0;}}&:has(:where(", ",", ")):not( :has(:where(", ",", "):enabled) ) :where(", ",", "){color:", ";cursor:not-allowed;}& :where(", ",", "){margin-inline-start:", ";}& ", " :where(", ",", "){margin-inline-start:0;}& > :where(", ",", ") + ", "{flex:0 1 calc(100% - (", " + 1.125rem));&::before{inset-inline-start:calc(-1 * (", " + 1.125rem));}}"], spacing4, spacing8, InternalUseOnlyLegend, spacing4, InternalUseOnlyLabel, spacing8, textColorPrimary, spacing8, ({
  $validityState
}) => $validityState === "error" ? valuesByValidityState.error.borderColor : "transparent", InternalUseOnlyRadio, InternalUseOnlyCheckbox, InternalUseOnlyRadio, InternalUseOnlyCheckbox, InternalUseOnlyLegend, InternalUseOnlyHint, textColorTertiary, InternalUseOnlyRadio, InternalUseOnlyCheckbox, spacing8, InternalUseOnlyLabel, InternalUseOnlyRadio, InternalUseOnlyCheckbox, InternalUseOnlyRadio, InternalUseOnlyCheckbox, InternalUseOnlyLabel, spacing8, spacing8);

/**
 * The `FieldSet` is used to group `Radio` and `Checkbox` components with helper components that describe them.
 * @param {object} props - The component props
 * @param {string} [props.aria-describedby] - Identifies the element (or elements) that describes the FieldSet
 * @param {React.ReactNode} props.children - Content to render inside the FieldSet Component. Should contain `Radio` or `Checkbox` form controls each with their `Label`.
 * @param {React.ReactNode} [props.description] - A brief description of how to fill in the FieldSet
 * @param {string} [props.id] - An identifier which must be unique in the whole document
 * @param {React.ReactNode} [props.legend] - A legend for the Fieldset. Will automatically add a required indicator if children are determined to be required.
 * @param {Function} [props.onValidate] - Callback for handling custom error, and warning messages
 * @param {React.ReactNode} [props.tooltip] - A long description of the FieldSet hidden until user interaction
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The FieldSet component
 */
const FieldSetComponent = ({
  "aria-describedby": ariaDescribedBy,
  children,
  description,
  id,
  legend,
  onValidate,
  tooltip,
  ...props
}, ref) => {
  const fieldSetId = useId();
  const hintId = useId();
  const tooltipCalloutId = useId();
  const {
    validationMessage,
    warningMessage
  } = useValidation(id ? id : fieldSetId, onValidate);
  let hasHint = false;
  let hasLegend = false;
  let isRequired = false;
  deepMapChildren(children, child => {
    if (child?.type === Hint) {
      hasHint = true;
    }
    if (child?.type === Legend) {
      hasLegend = true;
    }
    if (child?.props?.required) {
      isRequired = true;
    }
  });
  if (hasLegend && (tooltip || legend)) {
    console.warn("A child Legend component is detected. The tooltip and legend property cannot be used.");
  }
  if (hasHint && description) {
    console.warn("A child Hint component is detected. The description property cannot be used.");
  }
  const showHint = !hasHint && (description || validationMessage || warningMessage);
  const validityState = getValidityState(validationMessage, warningMessage);
  const getDescribedBy = () => {
    if (ariaDescribedBy) {
      return ariaDescribedBy;
    }
    const ids = [];
    if (tooltip) {
      ids.push(tooltipCalloutId);
    }
    if (showHint) {
      ids.push(hintId);
    }
    return ids.length === 0 ? undefined : ids.join(" ");
  };
  return /*#__PURE__*/_jsx(Container, {
    $validityState: validityState,
    "aria-describedby": getDescribedBy(),
    id: id ? id : fieldSetId,
    ref: ref,
    ...props,
    children: /*#__PURE__*/_jsxs(ValidityStateContext.Provider, {
      value: validityState,
      children: [!hasLegend ? /*#__PURE__*/_jsxs(ConditionalWrapper, {
        condition: tooltip,
        wrapper: /*#__PURE__*/_jsx(Row, {
          verticalAlign: "center",
          gapSize: "4"
        }),
        children: [legend ? /*#__PURE__*/_jsxs(Legend, {
          children: [isRequired ? /*#__PURE__*/_jsx(RequiredIndicator, {}) : null, legend]
        }) : null, tooltip ? /*#__PURE__*/_jsx(Tooltip, {
          calloutId: tooltipCalloutId,
          children: tooltip
        }) : null]
      }) : null, children, showHint ? /*#__PURE__*/_jsxs(Hint, {
        id: hintId,
        children: [validationMessage ? validationMessage : null, !validationMessage && warningMessage ? warningMessage : null, description ? validationMessage || warningMessage ? " " + description : description : null]
      }) : null]
    })
  });
};
const FieldSet = /*#__PURE__*/forwardRef(FieldSetComponent);
FieldSet.displayName = "FieldSet";
export default FieldSet;